import { useMemo } from 'react';

import { useGetAvailableDimensions, useGetAvailableMetrics } from '../../../../api/generated/api';
import type {
  DimensionMetadataDto,
  MetricMetadataDto,
  ReportingGrainsDto,
} from '../../../../api/generated/models';

export interface IReportingMetadata {
  dimensions: DimensionMetadataDto[];
  metrics: MetricMetadataDto[];
  grains: ReportingGrainsDto;

  labels: Map<string, string>;
  loaded: boolean;
}

// Module-level so the not-loaded fallbacks keep a stable identity; a fresh [] per
// render would recompute every downstream memo.
const EMPTY_GRAINS: ReportingGrainsDto = { preaggregated: [], servedRollup: [] };
const EMPTY_DIMENSIONS: DimensionMetadataDto[] = [];
const EMPTY_METRICS: MetricMetadataDto[] = [];

export const useReportingMetadata = (): IReportingMetadata => {
  const dimensionsQuery = useGetAvailableDimensions();
  const metricsQuery = useGetAvailableMetrics();

  const dimensions = dimensionsQuery.data?.dimensions ?? EMPTY_DIMENSIONS;
  const metrics = metricsQuery.data?.metrics ?? EMPTY_METRICS;
  const grains = dimensionsQuery.data?.grains ?? EMPTY_GRAINS;
  const loaded = Boolean(dimensionsQuery.data && metricsQuery.data);

  const labels = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of dimensions) map.set(d.key, d.label);
    for (const m of metrics) map.set(m.key, m.label);
    return map;
  }, [dimensions, metrics]);

  return useMemo(
    () => ({ dimensions, metrics, grains, labels, loaded }),
    [dimensions, metrics, grains, labels, loaded],
  );
};
